import { each, first, filter, isNil, isEmpty, get } from 'lodash';
import DateTimeFormatHelper from '../DateTimePickerHelperWrapper';
import validator from 'validator';
import { IUserCourse } from 'src/pages/Home/packages/CourseButton';

interface IUserPermission {
    module: string;
    permissionsList: IPermissionList[];
}

interface IPermissionList {
    displayName: string;
    permission: string;
    severity: string;
    _id: string;
}

export default class Utility {
    static readonly zipCodeFormat: any = {
        'en-US': '^\\d{5}(-\\d{4})?$',
    };

    static validateZipCode(val: string) {
        const userLocale = navigator?.languages?.length ? navigator.languages[0] : navigator.language;
        const regex = new RegExp(this.zipCodeFormat[userLocale] || this.zipCodeFormat['en-US']);

        return regex.test(val);
    }

    static validateFields(fields: any, validatedFields: string[], customMessage?: any) {
        const errors: { [key: string]: string } = {};
        each(fields, (v: any, k: string) => {
            const value = first(filter(validatedFields, (val: string) => val === k));
            if ((value && isNil(v)) || (value && v === '')) {
                errors[k] = this.getLabel(value, customMessage);
            }
        });

        return errors;
    }

    static isNoEmptyFields(requiredFieldKeys: string[], lookupData: any) {
        if (requiredFieldKeys.filter((field: string) => isEmpty(lookupData[field])).length > 0) return false;
        return true;
    }

    static getLabel(val: string, customMessage?: any): string {
        if (isEmpty(customMessage)) {
            switch (val) {
                case 'image':
                case 'imageFile':
                    return `Please select the ${val}`;
                default:
                    return `Please enter the ${val}`;
            }
        } else {
            return get(customMessage, val, `Please enter the ${val}`);
        }
    }

    static isMobileView() {
        return /(iPhone|iPad|iPod|Android)/.test(navigator.userAgent);
    }

    static getRandomVal() {
        const array = new Uint32Array(1);
        window.crypto.getRandomValues(array);
        return array[0] / (0xffffffff + 1);
    }

    static getRandomValWithMaxValue(maxValue: number, isInclusive = false) {
        const array = new Uint32Array(1);
        window.crypto.getRandomValues(array);
        const range = isInclusive ? maxValue + 1 : maxValue;
        return Math.floor((array[0] / (0xffffffff + 1)) * range);
    }

    static getMaintenanceDuration(startTime?: string, userRestrictedTime?: number) {
        const diff = DateTimeFormatHelper.diff(
            DateTimeFormatHelper.currentDate(),
            DateTimeFormatHelper.getDate(startTime),
            'second',
        );

        const hoursDifference = Math.floor(diff / 3600);
        const minutesDifference = Math.floor((diff % 3600) / 60);
        const secondsDifference = diff % 60;
        const restrictDuration = userRestrictedTime ?? 2;

        if (
            (hoursDifference >= 0 && hoursDifference < restrictDuration) ||
            (hoursDifference === restrictDuration && minutesDifference <= 0 && secondsDifference <= 0)
        ) {
            return true;
        } else {
            return false;
        }
    }

    static validateValueForRegex(regex: RegExp, value?: string) {
        let isValid = false;

        if (!value || regex.test(value)) {
            isValid = true;
        }
        return isValid;
    }

    static getNumberSuffix(number: number) {
        const lastDigit = number % 10;
        let suffix;

        if (lastDigit === 1) {
            suffix = 'st';
        } else if (lastDigit === 2) {
            suffix = 'nd';
        } else if (lastDigit === 3) {
            suffix = 'rd';
        } else {
            suffix = 'th';
        }

        return `${number}${suffix}`;
    }

    static readonly validateEmail = (email: string) => {
        return email && validator.isEmail(email);
    };

    static readonly validateNmlsUserId = (value?: string) => {
        let isValid = false;

        if (!value || this.validateEmail(value) || Utility.validateValueForRegex(/^\d{4,9}$/, value)) {
            isValid = true;
        }

        return isValid;
    };

    static readonly validateNmlsCourseId = (value?: string) => {
        return Utility.validateValueForRegex(/^\d{4,7}$/, value);
    };

    static readonly validateNmlsCourseDuration = (value?: string) => {
        return Utility.validateValueForRegex(/^[1-9]$|^1[0-2]$/, value);
    };

    static readonly getUsersPermissions = (moduleName: string) => {
        const user = localStorage.getItem('user');
        const parsedUser =
            !isNil(user) && !isEmpty(user)
                ? (JSON.parse(user)
                      ?.userGroupPermissions?.allPermissions.find(
                          (userPermission: IUserPermission) => userPermission.module === moduleName,
                      )
                      ?.permissionsList.map((list: IPermissionList) => list.permission) ?? [])
                : [];

        return parsedUser;
    };

    static readonly calculateRemainingAccessibleDays = (userCourse: IUserCourse) => {
        let remainingAccessibleDays = 0;

        const daysFromCreatedAt = DateTimeFormatHelper.diff(
            DateTimeFormatHelper.getDate(userCourse.createdAt),
            DateTimeFormatHelper.currentDate(),
            'days',
        );

        const daysFromExpiredAt = DateTimeFormatHelper.diff(
            DateTimeFormatHelper.getDate(userCourse.createdAt),
            DateTimeFormatHelper.getDate(userCourse.expiresAt),
            'days',
        );

        const comparingDays =
            daysFromExpiredAt > daysFromCreatedAt && daysFromExpiredAt >= 0 ? daysFromExpiredAt : daysFromCreatedAt + 1;

        if (comparingDays < userCourse.maximumCourseTimeFrame) {
            remainingAccessibleDays = userCourse.maximumCourseTimeFrame - comparingDays;
        }

        return remainingAccessibleDays;
    };

    public static readonly buildQueryString = (params?: Record<string, any>): string => {
        if (!params || Object.keys(params).length === 0) return '';
        return '?' + new URLSearchParams(params).toString();
    };

    static readonly convertTime = (time: number) => {
        time = time * 60;
        const hour = Math.floor(time / 3600);
        const min = Math.floor((time % 3600) / 60);
        const seconds = Math.floor((time % 3600) % 60);

        let timeString = '';
        if (hour > 0) {
            timeString += `${hour}h `;
        }
        timeString += `${min}m`;
        if (seconds) {
            timeString += ` ${seconds}s`;
        }
        return timeString;
    };

    static readonly convertTimeInMinutes = (time: number) => {
        const hour = Math.floor(time / 3600);
        const min = Math.floor((time % 3600) / 60);
        const seconds = Math.floor((time % 3600) % 60);

        let timeString = '';
        if (hour > 0) {
            timeString += `${hour}h `;
        }
        timeString += `${min}m`;
        if (seconds) {
            timeString += ` ${seconds}s`;
        }
        return timeString;
    };

    /**
     * Converts time in seconds to a formatted string with hours, minutes, and seconds.
     * If there are no hours, it includes minutes and seconds only.
     * Examples:
     * - 3661 -> "1h 1m"
     * - 59 -> "59s"
     * - 65 -> "1m 5s"
     */
    static readonly getFormattedTime = (time: number) => {
        const hour = Math.floor(time / 3600);
        const min = Math.floor((time % 3600) / 60);
        const seconds = Math.floor((time % 3600) % 60);

        let timeString = '';

        if (hour > 0) {
            timeString += `${hour}h `;
            timeString += `${min}m`;
        } else {
            timeString += `${min}m`;
            if (seconds > 0) {
                timeString += ` ${seconds}s`;
            }
        }

        return timeString;
    };

    static formatTime = (minutes: number) => {
        if (!minutes) return '0m 0s';

        const totalSeconds = Math.round(minutes * 60);
        const hours = Math.floor(totalSeconds / 3600);
        const mins = Math.floor((totalSeconds % 3600) / 60);
        const secs = totalSeconds % 60;

        if (hours > 0) {
            return `${hours}h ${mins}m`;
        } else {
            return `${mins}m ${secs}s`;
        }
    };
}
